import { useGetStore } from '@/composables/useGetStore'
import type {
	HpRequestOptional,
	HpRequestPostPut,
	HpService,
} from '@/services/serviceTypes'
import { Skeletons } from '@/stores/skeletons'
import { useAlertStore, useUserStore } from '@/stores/system'
import { useQueryClient } from '@tanstack/vue-query'
import { cloneDeep } from 'lodash'
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { get } from 'idb-keyval'

type EndpointOptions = {
	loadModules?: { store: string; id: string; item: string }[]
	hasModules?: { store: string; name: string }[]
	reloadItemOnRemove?: { id: string; store: string }[]
	options?: {
		saveCreated?: boolean
		collectionUpdate?: boolean
		parseIdAsInt?: boolean
		itemRemove?: boolean
		postOnPut?: boolean
	}
}

const getSkeleton = (name: string) => {
	return Skeletons[name as keyof typeof Skeletons] || null
}

export const useItem = <Model = any, PostInput = any, PutInput = any>(
	service: HpService,
	collectionName: string,
	skeletonName?: string | null,
	{
		loadModules = [],
		hasModules = [],
		reloadItemOnRemove = [],
		options,
	}: EndpointOptions = {}
) => {
	const finalOptions = {
		saveCreated: true,
		parseIdAsInt: true,
		collectionUpdate: true,
		itemRemove: true,
		postOnPut: false,
		...options,
	}

	const items = ref<Model[]>([])
	const pending = ref<Model[]>([])
	const status = ref<Api.Status>('initial')

	const { getStore } = useGetStore()

	const queryClient = useQueryClient()

	function getItem(id: number | string, withSkeleton = true) {
		const parsedId =
			finalOptions.parseIdAsInt && typeof id === 'string'
				? parseInt(id.toString())
				: id

		const foundItem = items.value.find((item) => item.id === parsedId)

		if (!foundItem) {
			const skeleton = getSkeleton(skeletonName)
			if (!skeleton || !withSkeleton) {
				if (!withSkeleton) {
					console.warn(
						`Skeleton ${skeletonName} for ${collectionName} not found`
					)
					return undefined
				} else {
					return {} as Model
				}
			}
			return skeleton
		}

		return foundItem
	}
	function isPending(id: number | string) {
		return pending.value.findIndex((item) => item.id === id)
	}

	function itemAdd(data: Model) {
		const index = items.value.findIndex(
			(item) =>
				item.id === (finalOptions.parseIdAsInt ? parseInt(data.id) : data.id)
		)
		if (index === -1) {
			items.value.push(data)
		} else {
			items.value.splice(index, 1, data)
		}
	}
	function itemRemove(id: number | string) {
		items.value.splice(items.value.find((x) => x.id === id))
	}
	function pendingAdd(data: unknown) {
		pending.value.push(data)
	}
	function pendingRemove(id: number | string) {
		pending.value.splice(items.value.find((x) => x.id === id))
	}
	function statusSet(value: Api.Status) {
		status.value = value
	}

	function waitForPending(id: number) {
		return new Promise((resolve, reject) => {
			let i = 0
			let item
			const intr = setInterval(function () {
				item = getItem(id)
				if (++i === 20 || item.id !== 0) {
					clearInterval(intr)
					if (item.id !== 0) {
						return resolve(item)
					}
					return reject()
				}
			}, 500)
		})
	}

	async function show({ id, force = false }: { id: any; force?: boolean }) {
		const { isOnline } = storeToRefs(useUserStore())
		if (typeof id === 'undefined' || id === 0) {
			console.warn('id is undefined or 0')
			return null
		}
		return new Promise((resolve) => {
			if (!force) {
				const item = getItem(id)
				if (item && typeof item.id !== 'undefined' && item.id !== 0) {
					return resolve(item)
				}
			}
			if (!isOnline.value && !import.meta.env.PROD) {
				console.log('Fetching item from IndexedDB', collectionName, id)
				return get(collectionName).then((values) => {
					if (!values || !Array.isArray(values)) {
						console.warn('No values found in IndexedDB for', collectionName)
						statusSet('error')
						return resolve(null)
					}
					const foundItem = values?.find((item) => item?.id === id)
					if (foundItem) {
						console.log('Found item in IndexedDB', foundItem)
						itemAdd(foundItem)
						statusSet('ready')
						return resolve(foundItem)
					}
					console.warn('Item not found in IndexedDB', id)
					statusSet('error')
					return resolve(null)
				})
			}

			if (isPending(id) >= 0) {
				return resolve(
					waitForPending(id).then((item) => {
						pendingRemove(id)
						return item
					})
				)
			}
			statusSet('loading')
			pendingAdd(id)
			return resolve(
				service
					.show(id)
					.then(({ data }) => {
						const item = cloneDeep(data)
						return Promise.all(
							loadModules.map((module) => {
								try {
									const moduleStore = getStore(module.store)
									return moduleStore
										.show({ id: item[module.id] })
										.then((data) => {
											return (item[module.item] = cloneDeep(data.data))
										})
								} catch (error) {
									//
								}
							})
						).then(() => {
							hasModules.map((module) => {
								if (Array.isArray(data[module.name])) {
									data[module.name].map((item) => {
										itemAdd(item)
									})
								}
							})
							itemAdd(item)
							statusSet('ready')
							return item
						})
					})
					/*.catch((error) => {
                        dispatch('error', error, { root: true })
                        return error
                    })*/
					.finally(() => {
						pendingRemove(id)
					})
			)
		})
	}

	async function put(
		data: HpRequestPostPut<PutInput> & {
			id: number
			throwError?: boolean
			itemRemove?: boolean
			collectionUpdate?: boolean
		}
	) {
		const { id, itemRemove, collectionUpdate, throwError, ...input } = data
		try {
			const item = await service.update(id, {
				...input,
				method: finalOptions.postOnPut ? 'post' : 'put',
			})
			if (finalOptions.itemRemove && itemRemove !== false) {
				await show({ id, force: true })
			}
			if (finalOptions.collectionUpdate && collectionUpdate !== false) {
				await invalidateData()
			}
			statusSet('ready')
			return item
		} catch (error) {
			useAlertStore().errorAlert(error)
			if (throwError) {
				throw error
			}
		}
	}

	async function post(data: HpRequestPostPut<PostInput>) {
		statusSet('loading')
		try {
			const item = await service.create(data)
			if (finalOptions.collectionUpdate) {
				await invalidateData(false)
			}
			statusSet('ready')
			if (finalOptions.saveCreated) {
				itemAdd(data)
			}
			return item
		} catch (error) {
			useAlertStore().errorAlert(error)
			throw error
		}
	}

	async function rem(data: HpRequestOptional & { id: number }) {
		statusSet('loading')
		const { id, ...input } = data
		return service.delete(id, input).then(
			(result) => {
				hasModules.map((module) => {
					// setCollectionReloads(module.store) //TODO
				})
				if (data?.reload === false) {
					return result
				}
				return Promise.all(
					reloadItemOnRemove.map((module) => {
						const item = getItem(id)
						if (
							typeof item === 'undefined' ||
							isNaN(module.id) ||
							module.id === 0
						) {
							return
						}
						service.show(item[module.id]).then((data) => {
							//FIXME ist kein ersatz zu der vorherigen implementation
							return data
						})
					})
				).then(async () => {
					itemRemove(id)
					await invalidateData()
					statusSet('ready')
					return result
				})
			},
			(error) => {
				useAlertStore().errorAlert(error)
				throw error
			}
		)
	}

	function attachTo({ id, target, targetId }: Api.AttachRequest) {
		service.attach(id, target, targetId).then(
			() => {
				statusSet('ready')
			},
			(error) => {
				useAlertStore().errorAlert(error)
				throw error
			}
		)
	}

	function detachFrom({ id, target, targetId }: Api.DetachRequest) {
		return service.detach(id, target, targetId).then(
			() => {
				statusSet('ready')
			},
			(error) => {
				useAlertStore().errorAlert(error)
				throw error
			}
		)
	}

	async function invalidateData(useReload = true) {
		await queryClient.invalidateQueries({
			queryKey: [collectionName],
		})
		if (useReload) await reload()
	}

	async function reload() {
		await Promise.all(
			items.value.map((item) => show({ id: item.id, force: true }))
		)
	}

	function reset() {
		items.value = []
		pending.value = []
		status.value = 'initial'
	}

	return {
		getStore,
		items,
		pending,
		status,
		getItem,
		itemAdd,
		itemRemove,
		isPending,
		show,
		create: post,
		update: put,
		remove: rem,
		attach: attachTo,
		detach: detachFrom,
		invalidateData,
		reload,
		reset,
	}
}
